import React, { useEffect, useMemo, useRef, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { Location, navigate } from '@reach/router';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Close from '@mui/icons-material/Close';
import HelpOutline from '@mui/icons-material/HelpOutline';
import OpenInFull from '@mui/icons-material/OpenInFull';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LockOutlined from '@mui/icons-material/LockOutlined';
import SmsOutlined from '@mui/icons-material/SmsOutlined';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import LanguageOutlined from '@mui/icons-material/LanguageOutlined';
import NotesOutlined from '@mui/icons-material/NotesOutlined';
import HtmlEditor from '../feature-catalog-admin/HtmlEditor';
import SearchableSelect from '../common/SearchableSelect';
import { FeatureGlyph } from '../features/FeatureMarkers';
import { MessageControlHelpButton } from './MessageControlHelpGuide';
import { MessageWhenHelpButton } from './MessageWhenHelp';
import MessagePreview from './MessagePreview';
import ThemedConfirmDialog from '../common/ThemedConfirmDialog';
import { toast } from 'react-toastify';
import {
  MESSAGE_TOKENS,
  SAMPLE_PHARMACY_HOURS_HTML,
  SAMPLE_KIOSK_HOURS_HTML,
  findMessageToken,
  tokenNeedsStorePicker,
  tokenWidgetHtml,
  buildSampleQrHtml,
  formatExpiryInDays,
  injectQrCode,
  serializeQrCode,
} from './tokens';
import {
  buildSampleOrderSummaryHtml,
  buildSampleOrderSummaryText,
  htmlHasOrderSummary,
  parseSummaryConfig,
  setSummaryConfig,
} from './orderSummary';
import AccountLocationPickerDialog from './AccountLocationPicker';
import EmailLayoutTools from './EmailLayoutTools';
import {
  DEFAULT_BRAND_COLORS,
  LAYOUT_CARDS,
  applyBrandColorsToHtml,
  applyBodyTextToHtml,
  applyCardFillToHtml,
  buildCardHtml,
  ensureEmailFooter,
  injectAccountLogo,
  serializeAccountLogo,
  accountLogoImgHtml,
  insertMissingFooterParts,
  insertPayNowBelowPayment,
  payNowBlockHtml,
  stripPaymentContent,
  rewriteEmailIconUrls,
  resolveAccountLogoUrl,
  setHideAmountDue,
  normalizeHex,
  parseBrandColors,
} from './layoutCards';
import {
  firstLocationInLineage,
  idsEqual,
  locationParentAccountName,
  locationParentLogoUrl,
  locationsInAccountLineage,
} from './lineage';
import {
  CUSTOM_BRAND_MESSAGING_KEY,
  fetchFeatureLocations,
  findEntitlement,
  getFeatureLockedRedirectPath,
  getSelectedAccountId,
  getSelectedBoxId,
  isFeatureUsable,
} from '../../utils/productLicensing';
import {
  fetchMessageAccounts,
  fetchMessageOverview,
  fetchMessageTemplates,
  loadMessagingEntitlements,
  publishMessageTemplates,
  restoreMessageTemplate,
  saveMessageDraft,
  saveMessageNote,
  setMessageTemplateDefault,
  deleteMessageNote,
  uploadMessageImage,
} from './api';
import { eventsForMethod, eventLabel, eventWhen, DEFAULT_EVENT } from './events';
import {
  DEFAULT_LINK_COLORS,
  DEFAULT_LINK_CTA,
  DEFAULT_LINK_HEADLINE,
  DEFAULT_LINK_INTRO,
  LINK_CUSTOM_MESSAGE_MAX,
  isLegacyLinkDraft,
  parseMobileConfig,
} from './mobileConfig';
import './message-control-center.css';
import '../features/features.css';

const CHANNELS = ['sms', 'email', 'mobile', 'notes'];

const ActionSpinner = ({ active }) =>
  active ? <CircularProgress size={16} color="inherit" /> : null;

const PreviewExpandButton = ({ t, onClick }) => (
  <Button
    type="button"
    size="small"
    className="mcc-preview-expand"
    startIcon={<OpenInFull fontSize="small" />}
    onClick={onClick}
  >
    {t('MessageControl.FullScreenPreview', { defaultValue: 'Full screen' })}
  </Button>
);

const FALLBACK_FEATURE_BODY = `
<p>Brand the SMS, email, and patient-link messages this pharmacy already sends. Templates are per location, delivery method, and event — Counter, Curbside, Bedside, Courier, locker, Mail Order, and Other. Staff welcome, password, OTP, and kiosk-offline mail use the same Studio. Pickup code, hours, payment link, STOP, and the Powered by ilocalbox.com footer stay locked.</p>
<div class="feature-detail-sections">
<div class="feature-detail-section">
<p><strong>What this includes</strong></p>
<ul>
<li>Studio: Email, SMS, Link, and Staff templates for every location</li>
<li>Templates per licensed method, including Mail Order and Other</li>
<li>Staff mail: welcome, password, OTP, and kiosk offline</li>
<li>HTML editor, tokens, live preview, branding colors, and pharmacy logo</li>
<li>Custom events, order-summary columns, default + alternate custom notes, and deliver-time override</li>
</ul>
</div>
<div class="feature-detail-section">
<p><strong>What this does not include</strong></p>
<ul>
<li>Instant enablement (iLocal turns it on after the sale)</li>
<li>The delivery methods themselves (Counter, locker, and the rest are separate licenses)</li>
<li>Removal of required compliance language (STOP, pickup code, Powered by footer)</li>
<li>Campaigns or bulk promotional SMS. That is a separate product under Products → Campaigns.</li>
</ul>
</div>
</div>
`.trim();

const INCLUDE_HEADING = '<p><strong>What this includes</strong></p>';
const EXCLUDE_HEADING = '<p><strong>What this does not include</strong></p>';

const withSideBySideSections = (html) => {
  const body = String(html || '');
  if (!body || body.indexOf('feature-detail-sections') !== -1) {
    return body;
  }
  const includeIdx = body.indexOf(INCLUDE_HEADING);
  const excludeIdx = body.indexOf(EXCLUDE_HEADING);
  if (includeIdx === -1 || excludeIdx === -1 || excludeIdx <= includeIdx) {
    return body;
  }
  return (
    body.slice(0, includeIdx) +
    '<div class="feature-detail-sections">' +
    '<div class="feature-detail-section">' +
    body.slice(includeIdx, excludeIdx) +
    '</div>' +
    '<div class="feature-detail-section">' +
    body.slice(excludeIdx) +
    '</div>' +
    '</div>'
  );
};

const METHOD_HELP = {
  counter: 'In-store pickup SMS, email, and the patient link page.',
  curbside: 'Parking-lot pickup messages and I’m-here link copy.',
  bedside: 'Discharge / bedside delivery messages.',
  courier: 'Out-for-delivery messages while the order is on the way.',
  kiosk: 'Locker pickup messages for this location.',
  mail_order: 'Mail-order messages while the prescription is on the way.',
  other: 'Pickup messages for this other location type.',
  account: 'Staff welcome, password, OTP, and kiosk-offline mail.',
};

const MessageControlCenterPage = ({ t, location }) => {
  const search = (location && location.search) || '';
  const query = useMemo(() => {
    try {
      const params = new URLSearchParams(search);
      const type = String(params.get('type') || '').toLowerCase();
      const nextChannel = String(params.get('channel') || '').toLowerCase();
      const event = String(params.get('event') || params.get('event_key') || '')
        .trim()
        .toLowerCase();
      return {
        type,
        channel: CHANNELS.indexOf(nextChannel) !== -1 ? nextChannel : '',
        event,
        boxId: params.get('box_id') || '',
        from: String(params.get('from') || ''),
      };
    } catch (_err) {
      return { type: '', channel: '', event: '', boxId: '', from: '' };
    }
  }, [search]);
  const [loading, setLoading] = useState(query.from !== 'list');
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState(getSelectedAccountId() || '');
  const [locations, setLocations] = useState([]);
  const [boxId, setBoxId] = useState(query.boxId || getSelectedBoxId() || '');
  const [overview, setOverview] = useState(null);
  const [featureCopy, setFeatureCopy] = useState(null);
  const [method, setMethod] = useState(query.type || '');
  const [eventKey, setEventKey] = useState(
    query.event || (query.from === 'list' ? DEFAULT_EVENT : '')
  );
  const [channel, setChannel] = useState(query.channel || 'sms');
  const [sourceView, setSourceView] = useState(false);
  const [fullPreviewOpen, setFullPreviewOpen] = useState(false);
  const [templates, setTemplates] = useState(null);
  const [html, setHtml] = useState('');
  const [subject, setSubject] = useState('');
  const [brandColors, setBrandColors] = useState(DEFAULT_BRAND_COLORS);
  const [headline, setHeadline] = useState('');
  const [intro, setIntro] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');
  const [greeting, setGreeting] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [arrivalLabel, setArrivalLabel] = useState('');
  const [linkColors, setLinkColors] = useState(DEFAULT_LINK_COLORS);
  const [hideAmountDueLink, setHideAmountDueLink] = useState(false);
  const [linkLogoUrl, setLinkLogoUrl] = useState('');
  const [linkLogoBusy, setLinkLogoBusy] = useState(false);
  const preview = null;
  const previewBusy = false;
  const [error, setError] = useState('');
  const [busyAction, setBusyAction] = useState('');
  const [busyNoteId, setBusyNoteId] = useState(null);
  const [overviewBusy, setOverviewBusy] = useState(false);
  const [noteDraft, setNoteDraft] = useState({ label: '', body: '' });
  const [tokenPicker, setTokenPicker] = useState(null);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [pickedPreview, setPickedPreview] = useState({
    accountName: '',
    locationName: '',
  });
  const editorRef = useRef(null);
  const linkLogoInputRef = useRef(null);
  const loadGen = useRef(0);
  const cleanGen = useRef(-1);
  const cleanSnap = useRef('');
  const [leavePrompt, setLeavePrompt] = useState(false);
  const openedDeepLink = useRef('');
  const defaultNote =
    (templates && templates.default_note && templates.default_note.body) || '';
  const locationName = useMemo(() => {
    const match = locations.find((item) => item.id === boxId);
    return (match && match.name) || t('MessageControl.Location', { defaultValue: 'Location' });
  }, [locations, boxId, t]);
  const selectedLocation = useMemo(
    () => locations.find((item) => idsEqual(item.id, boxId)),
    [locations, boxId]
  );
  const accountName = useMemo(() => {
    const fromLocation = locationParentAccountName(selectedLocation, accounts);
    if (fromLocation) {
      return fromLocation;
    }
    const match = accounts.find((item) =>
      idsEqual(item.id, selectedLocation && selectedLocation.account_id)
    );
    return (match && (match.name || match.brandName)) || 'Pharmacy';
  }, [accounts, selectedLocation]);
  const scopedLocations = useMemo(
    () => locationsInAccountLineage(locations, accounts, accountId),
    [locations, accounts, accountId]
  );
  const previewAccountName = pickedPreview.accountName || accountName;
  const previewLocationName = pickedPreview.locationName || locationName;
  const accountLogoUrl = useMemo(() => {
    const fromLocation = locationParentLogoUrl(selectedLocation, accounts);
    if (fromLocation) {
      return resolveAccountLogoUrl(fromLocation);
    }
    const match = accounts.find((item) =>
      idsEqual(item.id, selectedLocation && selectedLocation.account_id)
    );
    return resolveAccountLogoUrl(match && match.logoUrl);
  }, [accounts, selectedLocation]);

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      try {
        const [entitlementData, locationData] = await Promise.all([
          loadMessagingEntitlements(),
          fetchFeatureLocations(),
        ]);
        let storeList = [];
        try {
          storeList = await fetchMessageAccounts();
        } catch (_err) {
          storeList = [];
        }
        if (cancelled) return;
        const messaging = findEntitlement(
          (entitlementData && entitlementData.entitlements) || [],
          CUSTOM_BRAND_MESSAGING_KEY
        );
        if (!isFeatureUsable(messaging)) {
          navigate(getFeatureLockedRedirectPath(CUSTOM_BRAND_MESSAGING_KEY));
          return;
        }
        setFeatureCopy(messaging || null);
        const list = (locationData && locationData.locations) || [];
        const uniqueAccounts = storeList.length
          ? storeList
          : Array.from(
              list.reduce((map, item) => {
                if (item.account_id && !map.has(item.account_id)) {
                  map.set(item.account_id, {
                    id: item.account_id,
                    name: item.account_name || item.name,
                    account_lineage: '',
                    brandName: item.account_name || item.name,
                    logoUrl: item.logo_url || '',
                  });
                }
                return map;
              }, new Map()).values()
            );
        setLocations(list);
        setAccounts(uniqueAccounts);
        const requestedBoxId = query.boxId || boxId;
        const preferredBox =
          list.find((item) => idsEqual(item.id, requestedBoxId)) || list[0];
        const preferredAccount =
          (preferredBox && preferredBox.account_id) ||
          getSelectedAccountId() ||
          (uniqueAccounts[0] && uniqueAccounts[0].id) ||
          '';
        const scoped = locationsInAccountLineage(list, uniqueAccounts, preferredAccount);
        const nextBox =
          scoped.find((item) => idsEqual(item.id, preferredBox && preferredBox.id)) ||
          scoped[0];
        setAccountId(preferredAccount);
        setBoxId((nextBox && nextBox.id) || '');
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    boot();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!boxId) return;
    if (query.from === 'list') {
      setOverviewBusy(false);
      return undefined;
    }
    let cancelled = false;
    setOverviewBusy(true);
    fetchMessageOverview(boxId)
      .then((data) => {
        if (!cancelled) setOverview(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setOverviewBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [boxId, query.from]);

  useEffect(() => {
    if (query.boxId) setBoxId(query.boxId);
    if (query.type) setMethod(query.type);
    if (query.channel) setChannel(query.channel);
    if (query.event) setEventKey(query.event);
    else if (query.from === 'list' && query.type && !query.event) {
      setEventKey(DEFAULT_EVENT);
    }
  }, [query.boxId, query.type, query.channel, query.event, query.from]);

  useEffect(() => {
    if (channel !== 'email' || !html) {
      return undefined;
    }
    const next = rewriteEmailIconUrls(html);
    if (next !== html) {
      setHtml(next);
    }
    return undefined;
  }, [channel, html]);

  useEffect(() => {
    if (!fullPreviewOpen) {
      return undefined;
    }
    const onKey = (event) => {
      if (event.key === 'Escape') {
        setFullPreviewOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [fullPreviewOpen]);

  useEffect(() => {
    if (channel !== 'email' && channel !== 'sms') {
      return undefined;
    }
    setHtml((current) => {
      if (!current) {
        return current;
      }
      const next = injectAccountLogo(current, accountLogoUrl);
      return next === current ? current : next;
    });
    return undefined;
  }, [channel, accountLogoUrl]);

  const editorFingerprint = () =>
    JSON.stringify({
      channel,
      html,
      subject,
      greeting,
      customMessage,
      arrivalLabel,
      headline,
      intro,
      ctaLabel,
    });

  const applyChannelDraft = (data, nextChannel) => {
    if (!data) return;
    const key = nextChannel === 'notes' ? 'sms' : nextChannel;
    const current = data.channels && data.channels[key];
    const draft = current && current.draft;
    if (!draft) return;
    loadGen.current += 1;
    const paymentOn = data.payment_enabled !== false;
    if (nextChannel === 'sms' || nextChannel === 'email') {
      const rawBody = draft.html_body || '';
      const body = paymentOn ? rawBody : stripPaymentContent(rawBody);
      const storedColors = parseBrandColors(body);
      const emailBody =
        nextChannel === 'email'
          ? applyBodyTextToHtml(
              applyCardFillToHtml(body, storedColors.card),
              storedColors.text
            )
          : body;
      setHtml(
        nextChannel === 'email'
          ? injectAccountLogo(ensureEmailFooter(emailBody), accountLogoUrl)
          : injectQrCode(injectAccountLogo(body, accountLogoUrl), brandColors.header)
      );
      setSubject(draft.email_subject || '');
      if (nextChannel === 'email') {
        setBrandColors(parseBrandColors(body));
      }
    }
    if (nextChannel === 'mobile') {
      const config = parseMobileConfig(draft.html_body, method);
      const legacy = isLegacyLinkDraft(draft.html_body, draft.headline);
      setHeadline(legacy ? DEFAULT_LINK_HEADLINE : draft.headline || '');
      setIntro(legacy ? DEFAULT_LINK_INTRO : draft.intro || '');
      setCtaLabel(legacy ? DEFAULT_LINK_CTA : draft.cta_label || '');
      setGreeting(config.greeting || '');
      setCustomMessage(config.custom_message || '');
      setArrivalLabel(config.arrival_label || '');
      setLinkColors({
        button: config.button_color,
        accent: config.accent_color,
      });
      setHideAmountDueLink(paymentOn ? !!config.hide_amount : true);
      setLinkLogoUrl(config.logo_url || '');
    }
  };

  const loadTemplates = async (nextMethod, options = {}) => {
    const track = options.track !== false;
    const nextChannel = options.channel || channel;
    if (track) setBusyAction('load');
    setError('');
    try {
      const data = await fetchMessageTemplates(
        boxId,
        nextMethod,
        options.eventKey || eventKey || DEFAULT_EVENT
      );
      setTemplates(data);
      applyChannelDraft(data, nextChannel);
    } catch (err) {
      setError(err.message);
    } finally {
      if (track) setBusyAction('');
    }
  };

  const deepLinkKey = `${query.boxId}|${query.type}|${query.event}|${query.channel}`;
  useEffect(() => {
    if (!boxId || !method || !query.type) return;
    if (query.from !== 'list' && loading) return;
    const nextEvent =
      query.event || (query.from === 'list' ? DEFAULT_EVENT : eventKey);
    if (!nextEvent) return;
    if (openedDeepLink.current === deepLinkKey) return;
    openedDeepLink.current = deepLinkKey;
    loadTemplates(method, {
      channel: query.channel || channel,
      eventKey: nextEvent,
    });
  }, [loading, boxId, method, deepLinkKey]);

  useEffect(() => {
    if (cleanGen.current === loadGen.current) return;
    cleanGen.current = loadGen.current;
    cleanSnap.current = editorFingerprint();
  }, [
    html,
    subject,
    greeting,
    customMessage,
    arrivalLabel,
    headline,
    intro,
    ctaLabel,
    channel,
  ]);

  const closeEditor = () => {
    setLeavePrompt(false);
    setError('');
    setNoteToDelete(null);
    setFullPreviewOpen(false);
    if (query.from === 'list') {
      const listChannel = query.channel || channel || 'sms';
      navigate(`/home/manage/templates/${listChannel}`);
      return;
    }
    if (eventKey) {
      setEventKey('');
      setTemplates(null);
      setHtml('');
      return;
    }
    setMethod('');
  };

  useEffect(() => {
    if (!method || !templates) return;
    if (channel === 'notes') return;
    applyChannelDraft(templates, channel);
  }, [channel, method]);

  useEffect(() => {
    if (!method || !eventKey || !eventWhen(eventKey)) return;
    const offered = eventsForMethod(method).some((event) => event.key === eventKey);
    if (offered) return;
    const fallback = eventsForMethod(method)[0];
    setEventKey((fallback && fallback.key) || DEFAULT_EVENT);
  }, [method, eventKey]);

  useEffect(() => {
    if (query.from === 'list') return;
    const mobileKeys = eventsForMethod(method, 'mobile').map(
      (event) => event.key
    );
    if (
      eventKey &&
      mobileKeys.indexOf(eventKey) === -1 &&
      channel === 'mobile'
    ) {
      setChannel('sms');
    }
  }, [eventKey, channel, query.from, method]);

  const summaryConfig = parseSummaryConfig(html);
  const hasOrderSummary = htmlHasOrderSummary(html);
  const hideAmountDue = summaryConfig.hideAmount;
  const orderSummaryHtml = buildSampleOrderSummaryHtml({
    ...summaryConfig,
    textColor: brandColors.text,
  });
  const orderSummaryText = buildSampleOrderSummaryText(summaryConfig);
  const hoursPreview = (templates && templates.hours) || {};
  const locationDetail = (templates && templates.location) || {};
  const previewExpiry =
    locationDetail.expiry ||
    formatExpiryInDays(
      (templates && templates.expiry_days) ||
        (selectedLocation && selectedLocation.expiration_days)
    );
  const previewAddress =
    locationDetail.address || '100 Main St, Austin, TX';
  const previewPhone = locationDetail.phone || '(512) 555-0100';
  const previewBrandPhone = locationDetail.brand_phone || previewPhone;
  const previewBrandEmail =
    locationDetail.brand_email || 'pharmacy@example.com';
  const previewTokens = {
    'First Name': 'Maria',
    'Last Name': 'Garcia',
    'Full Name': 'Maria Garcia',
    'Account Name': previewAccountName,
    'Account Logo': `<img src="${String(accountLogoUrl).replace(
      /"/g,
      '&quot;'
    )}" alt="iLocalBox" height="48" style="max-height:48px;max-width:200px;width:auto;border:0;outline:none;display:block;margin:0 auto;background:#ffffff;border-radius:6px;padding:6px 10px;" />`,
    'Location Name': locationDetail.name || previewLocationName,
    'Location Address': previewAddress,
    'Location Phone': previewPhone,
    'Brand Phone': previewBrandPhone,
    'Brand Email': previewBrandEmail,
    'Pickup Code': '8E8QVW',
    Hours: hoursPreview.hours || 'Open 24/7',
    'Pharmacy Hours':
      hoursPreview.pharmacyHoursHtml || SAMPLE_PHARMACY_HOURS_HTML,
    'Kiosk Hours': hoursPreview.kioskHoursHtml || SAMPLE_KIOSK_HOURS_HTML,
    Link: 'ilocal.link/xxxx',
    'Expiry Date': previewExpiry,
    Website: locationDetail.website || 'ilocalbox.com',
    'QR Code': buildSampleQrHtml(brandColors.header),
    'Order Summary':
      channel === 'email' ? orderSummaryHtml : orderSummaryText,
  };

  const toggleHideAmount = (hide) => {
    setHtml((current) => setHideAmountDue(current, hide));
  };

  const applySummaryConfig = (next) => {
    setHtml((current) => setSummaryConfig(current, next));
  };

  const openMethod = (product) => {
    if (product.locked) return;
    setMethod(product.method);
    setEventKey('');
    setChannel('sms');
    setTemplates(null);
  };

  const openEvent = (event) => {
    const nextEvent = event.key || event.event_key;
    setEventKey(nextEvent);
    setChannel('sms');
    loadTemplates(method, { channel: 'sms', eventKey: nextEvent });
  };

  const currentEditorHtml = async () => {
    if (
      !sourceView &&
      editorRef.current &&
      typeof editorRef.current.flushContent === 'function'
    ) {
      const flushed = await editorRef.current.flushContent();
      if (typeof flushed === 'string') {
        return flushed;
      }
    }
    return html;
  };

  const buildDraftPayload = (htmlBody = html) => {
    const payload = {
      box_id: boxId,
      method,
      event_key: eventKey || DEFAULT_EVENT,
      channel: channel === 'notes' ? 'sms' : channel,
      html_body: serializeQrCode(serializeAccountLogo(htmlBody)),
      email_subject: subject,
      headline,
      intro,
      cta_label: ctaLabel,
    };
    if (channel === 'mobile') {
      payload.channel = 'mobile';
      payload.greeting = greeting;
      payload.custom_message = customMessage;
      payload.arrival_label = arrivalLabel;
      payload.button_color = linkColors.button;
      payload.accent_color = linkColors.accent;
      payload.hide_amount = paymentEnabled ? hideAmountDueLink : true;
      payload.logo_url = linkLogoUrl || '';
      delete payload.html_body;
    }
    return payload;
  };

  const saveDraft = async () => {
    setBusyAction('save');
    setError('');
    try {
      const htmlBody = await currentEditorHtml();
      await saveMessageDraft(buildDraftPayload(htmlBody));
      if (htmlBody && htmlBody !== html) {
        setHtml(htmlBody);
      }
      cleanSnap.current = JSON.stringify({
        channel,
        html: htmlBody || html,
        subject,
        greeting,
        customMessage,
        arrivalLabel,
        headline,
        intro,
        ctaLabel,
      });
      cleanGen.current = loadGen.current;
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setBusyAction('');
    }
  };

  const editorIsDirty = () =>
    !!eventKey &&
    channel !== 'notes' &&
    cleanSnap.current !== editorFingerprint();

  const requestClose = () => {
    if (busyAction) return;
    if (editorIsDirty()) {
      setLeavePrompt(true);
      return;
    }
    closeEditor();
  };

  const saveDraftAndClose = async () => {
    const saved = await saveDraft();
    if (!saved) return;
    closeEditor();
  };

  const closeWithoutSaving = () => {
    closeEditor();
  };

  const toggleDefaultTemplate = async (next) => {
    if (!boxId || !method || !eventKey || channel === 'notes') return;
    setBusyAction('default');
    setError('');
    try {
      await setMessageTemplateDefault({
        box_id: boxId,
        method,
        channel,
        event_key: eventKey || DEFAULT_EVENT,
        is_default: !!next,
      });
      await loadTemplates(method, {
        channel,
        eventKey: eventKey || DEFAULT_EVENT,
        track: false,
      });
      toast.success(
        next
          ? t('MessageControl.DefaultSaved', {
              defaultValue:
                'Saved. Orders still use a published template for their location type. This default is used only when that type has none.',
            })
          : t('MessageControl.DefaultCleared', {
              defaultValue:
                'Default removed. Sending uses the matching location-type template.',
            })
      );
    } catch (err) {
      toast.error(err.message);
      setError(err.message);
    } finally {
      setBusyAction('');
    }
  };

  const publish = async () => {
    setBusyAction('publish');
    setError('');
    try {
      const htmlBody = await currentEditorHtml();
      await saveMessageDraft(buildDraftPayload(htmlBody));
      await publishMessageTemplates({
        box_id: boxId,
        method,
        event_key: eventKey || DEFAULT_EVENT,
        channel: channel === 'notes' ? 'sms' : channel,
        // Sample QR markup is ~9KB of inline SVG. Do not send it here; the
        // gateway rejects bodies over 8KB for every template, not just this one.
        tokens: {
          firstName: 'Maria',
          lastName: 'Garcia',
          fullName: 'Maria Garcia',
          accountName: previewAccountName,
          locationName: locationDetail.name || previewLocationName,
          locationAddress: previewAddress,
          locationPhone: previewPhone,
          brandPhone: previewBrandPhone,
          brandEmail: previewBrandEmail,
          pickupCode: '8E8QVW',
          hours: 'Open 24/7',
          link: 'ilocal.link/xxxx',
          amount: paymentEnabled ? '$5.00' : '',
          expiryDate: previewExpiry,
        },
        custom_note:
          channel === 'sms'
            ? ''
            : (templates &&
                templates.default_note &&
                templates.default_note.body) ||
              '',
      });
      toast.success(
        t('MessageControl.PublishedToast', {
          defaultValue: 'Template published.',
        })
      );
      closeEditor();
    } catch (err) {
      setError(err.message || (err.data && err.data.message));
    } finally {
      setBusyAction('');
    }
  };

  const insertResolvedToken = (match) => {
    if (sourceView) {
      setHtml(`${html || ''}${tokenWidgetHtml(match.name, !!match.required)}`);
      return;
    }
    if (
      channel === 'sms' &&
      editorRef.current &&
      editorRef.current.insertHtml &&
      (match.name === 'Account Logo' || match.name === 'QR Code')
    ) {
      const picture =
        match.name === 'Account Logo'
          ? accountLogoImgHtml(accountLogoUrl)
          : injectQrCode(tokenWidgetHtml('QR Code'), brandColors.header);
      editorRef.current.insertHtml(picture);
      return;
    }
    if (editorRef.current && editorRef.current.insertToken) {
      editorRef.current.insertToken(match.name, !!match.required);
    }
  };

  const insertTokenAtCursor = (rawName) => {
    const match = findMessageToken(rawName);
    if (!match) return;
    if (tokenNeedsStorePicker(match)) {
      setTokenPicker(match);
      return;
    }
    if (paymentEnabled && channel === 'email' && match.name === 'Link') {
      insertLayoutCard('payment');
      return;
    }
    if (!paymentEnabled && match.name === 'Amount') {
      return;
    }
    insertResolvedToken(match);
  };

  const handleBrandColor = (key, value) => {
    const hex = normalizeHex(value, null);
    if (!hex) {
      setBrandColors({ ...brandColors, [key]: String(value || '') });
      return;
    }
    const next = { ...brandColors, [key]: hex };
    setBrandColors(next);
    setHtml(applyBrandColorsToHtml(html, next));
  };

  const handleLinkColor = (key, value) => {
    const hex = normalizeHex(value, null);
    if (!hex) {
      setLinkColors({ ...linkColors, [key]: String(value || '') });
      return;
    }
    setLinkColors({ ...linkColors, [key]: hex });
  };

  const uploadLinkLogo = async (file) => {
    if (!file || !boxId) return;
    setLinkLogoBusy(true);
    setError('');
    try {
      const url = await uploadMessageImage(file, boxId);
      if (url) {
        setLinkLogoUrl(url);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLinkLogoBusy(false);
    }
  };

  const insertLayoutCard = (cardId, dropEvent) => {
    const colors = {
      header: normalizeHex(brandColors.header, DEFAULT_BRAND_COLORS.header),
      button: normalizeHex(brandColors.button, DEFAULT_BRAND_COLORS.button),
      text: normalizeHex(brandColors.text, DEFAULT_BRAND_COLORS.text),
      card: normalizeHex(brandColors.card, DEFAULT_BRAND_COLORS.card),
    };
    if (cardId === 'footer') {
      const next = insertMissingFooterParts(html || '');
      if (next !== (html || '')) {
        setHtml(next);
      }
      return;
    }
    if (cardId === 'payment' || cardId === 'cta') {
      if (!paymentEnabled) return;
      if (editorRef.current && editorRef.current.insertPayNow) {
        if (editorRef.current.insertPayNow(payNowBlockHtml(colors))) {
          return;
        }
      }
      setHtml(insertPayNowBelowPayment(html || '', colors));
      return;
    }
    const snippet =
      cardId === 'summary'
        ? setSummaryConfig(
            buildCardHtml(cardId, colors),
            parseSummaryConfig(html)
          )
        : buildCardHtml(cardId, colors, accountLogoUrl);
    if (!snippet) {
      return;
    }
    if (sourceView) {
      setHtml(`${html || ''}${snippet}`);
      return;
    }
    if (editorRef.current && editorRef.current.insertHtml) {
      editorRef.current.insertHtml(snippet, {
        block: true,
        dropEvent,
      });
      return;
    }
    setHtml(`${html || ''}${snippet}`);
  };

  const restore = async () => {
    setBusyAction('restore');
    setError('');
    try {
      const draft = await restoreMessageTemplate({
        box_id: boxId,
        method,
        event_key: eventKey || DEFAULT_EVENT,
        channel: channel === 'notes' ? 'sms' : channel,
      });
      const rawBody = draft.html_body || '';
      const body = paymentEnabled ? rawBody : stripPaymentContent(rawBody);
      const storedColors = parseBrandColors(body);
      const emailBody =
        channel === 'email'
          ? applyBodyTextToHtml(
              applyCardFillToHtml(body, storedColors.card),
              storedColors.text
            )
          : body;
      setHtml(
        channel === 'email'
          ? injectAccountLogo(ensureEmailFooter(emailBody), accountLogoUrl)
          : channel === 'sms'
          ? injectQrCode(injectAccountLogo(body, accountLogoUrl), brandColors.header)
          : body
      );
      setSubject(draft.email_subject || '');
      setHeadline(draft.headline || '');
      setIntro(draft.intro || '');
      setCtaLabel(draft.cta_label || '');
      if (channel === 'mobile') {
        const config = parseMobileConfig(body, method);
        setGreeting(config.greeting || '');
        setCustomMessage(config.custom_message || '');
        setArrivalLabel(config.arrival_label || '');
        setLinkColors({
          button: config.button_color,
          accent: config.accent_color,
        });
        setHideAmountDueLink(paymentEnabled ? !!config.hide_amount : true);
        setLinkLogoUrl(config.logo_url || '');
      }
      if (channel === 'email') {
        setBrandColors(parseBrandColors(body));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyAction('');
    }
  };

  const saveNote = async (payload) => {
    setBusyAction('note-save');
    setBusyNoteId(payload.id || 'new');
    setError('');
    try {
      await saveMessageNote({
        box_id: boxId,
        method,
        ...payload,
      });
      await loadTemplates(method, { track: false });
      setNoteDraft({ label: '', body: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyAction('');
      setBusyNoteId(null);
    }
  };

  const deleteNote = async (note) => {
    if (!note || !note.id) return;
    setBusyAction('note-delete');
    setBusyNoteId(note.id);
    setError('');
    try {
      await deleteMessageNote({
        box_id: boxId,
        method,
        id: note.id,
      });
      setNoteToDelete(null);
      await loadTemplates(method, { track: false });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyAction('');
      setBusyNoteId(null);
    }
  };

  if (loading) {
    return (
      <div className="mcc-page mcc-page-loading">
        <CircularProgress />
        <p>
          {t('MessageControl.BusyLoadPage', {
            defaultValue: 'Loading Studio…',
          })}
        </p>
      </div>
    );
  }

  const products = (overview && overview.products) || [];
  const paymentEnabled =
    templates && typeof templates.payment_enabled === 'boolean'
      ? templates.payment_enabled
      : overview && typeof overview.payment_enabled === 'boolean'
        ? overview.payment_enabled
        : true;
  const editorTokens = MESSAGE_TOKENS.filter((token) => {
    if (!paymentEnabled && token.name === 'Amount') return false;
    if (
      method !== 'account' &&
      (token.name === 'OTP' ||
        token.name === 'Username' ||
        token.name === 'Temporary Password')
    ) {
      return false;
    }
    return true;
  });
  const editorCards = paymentEnabled
    ? LAYOUT_CARDS
    : LAYOUT_CARDS.filter((card) => card.id !== 'payment' && card.id !== 'cta');
  if (paymentEnabled) {
    previewTokens.Amount = '$12.40';
  }
  const notes = (templates && templates.notes) || [];
  const alternateNotes = notes.filter((note) => note.is_enabled && !note.is_default);
  const channelRecord =
    templates && templates.channels && templates.channels[channel];
  const isChannelDefault = !!(
    channelRecord &&
    (channelRecord.is_default ||
      (channelRecord.draft && channelRecord.draft.is_default) ||
      (channelRecord.published && channelRecord.published.is_default))
  );
  const renderDefaultToggle = () =>
    channel === 'notes' ? null : (
      <div className="mcc-default-toggle">
        <FormControlLabel
          control={
            <Switch
              checked={isChannelDefault}
              disabled={busy || !eventKey}
              color="primary"
              onChange={(event) =>
                toggleDefaultTemplate(event.target.checked)
              }
            />
          }
          label={t('MessageControl.DefaultTemplate', {
            defaultValue: 'Default for this location and event',
          })}
        />
        <p className="mcc-helper">
          {t('MessageControl.DefaultTemplateHelp', {
            defaultValue:
              'Orders use the published template for their location type. The default is sent only when that type has no published template.',
          })}
          {isChannelDefault &&
          channelRecord &&
          !channelRecord.published
            ? ' Publish it so the next email or SMS uses this copy.'
            : ''}
        </p>
      </div>
    );
  const canAddAlternate = alternateNotes.length < 5;
  const methodLabel =
    (products.find((item) => item.method === method) || {}).label || method;
  const renderLivePreview = (previewKey) => (
    <MessagePreview
      key={previewKey}
      channel={channel === 'notes' ? 'sms' : channel}
      preview={preview}
      previewBusy={previewBusy}
      html={html}
      subject={subject}
      accountName={previewAccountName}
      brandColors={brandColors}
      customNote={channel === 'email' || channel === 'sms' ? '' : defaultNote}
      tokens={previewTokens}
      headline={headline}
      intro={intro}
      ctaLabel={ctaLabel}
      greeting={greeting}
      customMessage={customMessage}
      arrivalLabel={arrivalLabel}
      locationName={previewLocationName}
      methodLabel={methodLabel}
      buttonColor={linkColors.button}
      accentColor={linkColors.accent}
      hideAmountDue={paymentEnabled ? hideAmountDueLink : true}
      paymentEnabled={paymentEnabled}
      logoUrl={linkLogoUrl}
      t={t}
    />
  );
  const renderPreviewPane = () => (
    <section
      className={
        'mcc-panel mcc-preview-pane' + (fullPreviewOpen ? ' is-expanded' : '')
      }
    >
      <div className="mcc-panel-head">
        <h2 className="mcc-panel-title">
          {t('MessageControl.Preview', { defaultValue: 'Preview' })}
        </h2>
        {fullPreviewOpen ? (
          <IconButton
            aria-label={t('MessageControl.Close', { defaultValue: 'Close' })}
            onClick={() => setFullPreviewOpen(false)}
            size="small"
          >
            <Close fontSize="small" />
          </IconButton>
        ) : (
          <PreviewExpandButton
            t={t}
            onClick={() => setFullPreviewOpen(true)}
          />
        )}
      </div>
      {renderLivePreview('pane')}
    </section>
  );
  const methodEvents = (() => {
    const catalog =
      ((products.find((item) => item.method === method) || {}).events || []).length
        ? (products.find((item) => item.method === method) || {}).events
        : eventsForMethod(method, channel === 'notes' ? 'sms' : channel);
    if (
      eventKey &&
      !eventWhen(eventKey) &&
      !catalog.some((event) => (event.event_key || event.key) === eventKey)
    ) {
      return [{ key: eventKey, label: eventLabel(eventKey, method) }].concat(
        catalog
      );
    }
    return catalog;
  })();
  const eventName = eventKey
    ? eventLabel(eventKey, method)
    : '';
  const featureBody = withSideBySideSections(
    (featureCopy && featureCopy.body) || FALLBACK_FEATURE_BODY
  );
  const busy = Boolean(busyAction);
  const deletingNote = busyAction === 'note-delete';
  const busyLabel = {
    load: t('MessageControl.BusyLoad', { defaultValue: 'Loading templates…' }),
    save: t('MessageControl.BusySave', { defaultValue: 'Saving draft…' }),
    publish: t('MessageControl.BusyPublish', { defaultValue: 'Publishing…' }),
    restore: t('MessageControl.BusyRestore', { defaultValue: 'Restoring default…' }),
    default: t('MessageControl.BusyDefault', {
      defaultValue: 'Updating default…',
    }),
    'note-save': t('MessageControl.BusyNoteSave', { defaultValue: 'Saving note…' }),
    'note-delete': t('MessageControl.BusyNoteDelete', {
      defaultValue: 'Deleting note…',
    }),
  }[busyAction] || '';

  return (
    <div className="mcc-page">
      <div className="mcc-hero">
        <span className="mcc-hero-icon">
          <FeatureGlyph featureKey={CUSTOM_BRAND_MESSAGING_KEY} />
        </span>
        <div className="mcc-hero-copy">
          <p className="mcc-hero-eyebrow">
            {t('MessageControl.ProductName', {
              defaultValue: 'Studio',
            })}
          </p>
          <h1>
            {t('MessageControl.Title', { defaultValue: 'Studio' })}
          </h1>
          <p className="mcc-helper">
            {t('MessageControl.Helper', {
              defaultValue:
                'Only licensed products can be customized. Templates are saved per location, delivery method, and notification event.',
            })}
          </p>
        </div>
        <div className="mcc-hero-actions">
          <MessageWhenHelpButton t={t} />
          <MessageControlHelpButton t={t} paymentEnabled={paymentEnabled} />
        </div>
      </div>

      <div className="mcc-workspace">
        {overviewBusy || busyAction === 'load' ? (
          <div className="mcc-inline-progress">
            <LinearProgress />
            <span>
              {busyAction === 'load'
                ? t('MessageControl.BusyLoad', {
                    defaultValue: 'Loading templates…',
                  })
                : t('MessageControl.BusyOverview', {
                    defaultValue: 'Loading location templates…',
                  })}
            </span>
          </div>
        ) : null}
        <div className="mcc-toolbar">
        <SearchableSelect
          id="mcc-account"
          label={t('MessageControl.Store', {
            defaultValue: 'Store (Account Name)',
          })}
          items={accounts}
          value={accountId || ''}
          placeholder={t('MessageControl.SearchStores', {
            defaultValue: 'Search stores...',
          })}
          variant="outlined"
          sx={{ minWidth: 0 }}
          onChange={(event) => {
            const nextAccountId = event.target.value;
            const nextBoxId = firstLocationInLineage(
              locations,
              accounts,
              nextAccountId
            );
            setAccountId(nextAccountId);
            setBoxId(nextBoxId);
            setPickedPreview({ accountName: '', locationName: '' });
            setMethod('');
            setEventKey('');
            setTemplates(null);
            setError('');
          }}
        />
        <SearchableSelect
          id="mcc-location"
          label={t('MessageControl.Location', {
            defaultValue: 'Location Name (Boxes)',
          })}
          items={scopedLocations}
          value={boxId || ''}
          placeholder={t('MessageControl.SearchLocations', {
            defaultValue: 'Search locations...',
          })}
          variant="outlined"
          sx={{ minWidth: 0 }}
          onChange={(event) => {
            const nextBoxId = event.target.value;
            const nextBox = scopedLocations.find((item) =>
              idsEqual(item.id, nextBoxId)
            );
            if (nextBox && nextBox.account_id) {
              setAccountId(nextBox.account_id);
            }
            setBoxId(nextBoxId);
            setPickedPreview({ accountName: '', locationName: '' });
            setMethod('');
            setEventKey('');
            setTemplates(null);
            setError('');
          }}
        />
        </div>
      {!method && error ? <p className="mcc-error">{error}</p> : null}
        <div className="mcc-tiles">
          {products.map((product) => {
            const published = !!(
              product.channels &&
              product.channels.sms &&
              product.channels.sms.published
            );
            const statusClass = product.locked
              ? ' is-locked'
              : published
                ? ' is-published'
                : '';
            return (
              <button
                type="button"
                key={product.method}
                className={`mcc-tile${product.locked ? ' is-locked' : ''}`}
                disabled={product.locked || overviewBusy}
                onClick={() => openMethod(product)}
              >
                <span className="mcc-tile-icon">
                  {product.locked ? (
                    <LockOutlined fontSize="small" />
                  ) : (
                    <FeatureGlyph
                      featureKey={product.feature_key || product.method}
                    />
                  )}
                </span>
                <h3>{product.label}</h3>
                <p className="mcc-tile-blurb">
                  {t(`MessageControl.MethodHelp.${product.method}`, {
                    defaultValue:
                      METHOD_HELP[product.method] ||
                      'SMS, email, and mobile-link copy for this method.',
                  })}
                </p>
                <div className={`mcc-tile-status${statusClass}`}>
                  {product.locked
                    ? t('MessageControl.LicenseRequired', {
                        defaultValue: 'License required',
                      })
                    : published
                      ? t('MessageControl.Published', {
                          defaultValue: 'Published',
                        })
                      : t('MessageControl.Draft', { defaultValue: 'Draft' })}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <details className="mcc-guide">
        <summary>
          <span>
            {t('MessageControl.ShowGuide', {
              defaultValue: 'What this includes and how to use this page',
            })}
          </span>
          <ExpandMore className="mcc-guide-chevron" fontSize="small" />
        </summary>
        <div className="mcc-guide-body">
          <div
            className="feature-detail-body"
            dangerouslySetInnerHTML={{ __html: featureBody }}
          />
          <div className="mcc-how">
            <h2>
              {t('MessageControl.HowTitle', {
                defaultValue: 'How to use this page',
              })}
            </h2>
            <ol className="mcc-how-list">
              <li>
                <span className="mcc-how-num">1</span>
                <div>
                  <strong>
                    {t('MessageControl.How1Title', {
                      defaultValue: 'Choose store and location',
                    })}
                  </strong>
                  <p>
                    {t('MessageControl.How1Body', {
                      defaultValue:
                        'Locations stay in the selected store’s lineage. Copy for one box does not change another store.',
                    })}
                  </p>
                </div>
              </li>
              <li>
                <span className="mcc-how-num">2</span>
                <div>
                  <strong>
                    {t('MessageControl.How2Title', {
                      defaultValue: 'Open a licensed method',
                    })}
                  </strong>
                  <p>
                    {t('MessageControl.How2Body', {
                      defaultValue:
                        'Only methods this account has licensed can be customized. Locked cards need that product license first.',
                    })}
                  </p>
                </div>
              </li>
              <li>
                <span className="mcc-how-num">3</span>
                <div>
                  <strong>
                    {t('MessageControl.How3Title', {
                      defaultValue: 'Edit SMS, email, mobile page, and notes',
                    })}
                  </strong>
                  <p>
                    {t('MessageControl.How3Body', {
                      defaultValue:
                        'Use tokens for live patient and store data. Custom notes are the pharmacy-specific line staff can swap on Deliver.',
                    })}
                  </p>
                </div>
              </li>
              <li>
                <span className="mcc-how-num">4</span>
                <div>
                  <strong>
                    {t('MessageControl.How4Title', {
                      defaultValue: 'Save draft, then publish',
                    })}
                  </strong>
                  <p>
                    {t('MessageControl.How4Body', {
                      defaultValue:
                        'Draft keeps work in progress. Publish is what the next patient SMS, email, and link page actually use.',
                    })}
                  </p>
                </div>
              </li>
            </ol>
          </div>
        </div>
      </details>

      <Dialog
        open={!!method}
        onClose={requestClose}
        fullScreen
        disableEnforceFocus
        disableAutoFocus
        PaperProps={{ className: 'mcc-dialog-paper' }}
      >
        <div className="mcc-dialog-shell">
          {busy ? <LinearProgress className="mcc-dialog-progress" /> : null}
          <DialogTitle className="mcc-dialog-header">
            <span className="mcc-dialog-title-text">
              {methodLabel}
              {eventName ? ` — ${eventName}` : ''}
              {' — '}
              {t('MessageControl.Templates', { defaultValue: 'Message templates' })}
              {' — '}
              {locationName}
            </span>
            <div className="mcc-dialog-header-tools">
              {busy ? (
                <span className="mcc-busy-status">
                  <CircularProgress size={18} />
                  {busyLabel}
                </span>
              ) : null}
              <MessageWhenHelpButton
                t={t}
                method={method}
                channel={channel === 'notes' ? 'sms' : channel}
                eventKey={eventKey}
                size="small"
              />
              <MessageControlHelpButton t={t} paymentEnabled={paymentEnabled} />
              <IconButton
                aria-label={t('MessageControl.Close', { defaultValue: 'Close' })}
                onClick={requestClose}
                disabled={busy}
                size="small"
              >
                <Close />
              </IconButton>
            </div>
          </DialogTitle>
          <DialogContent className="mcc-dialog-body">
            {!eventKey ? (
              <div className="mcc-event-picker">
                <div className="mcc-event-picker-head">
                  <p className="mcc-helper">
                    {t('MessageControl.EventPickerHelp', {
                      defaultValue:
                        'Choose the notification this template should send. Each event is published independently and does not replace the others.',
                    })}
                  </p>
                  <MessageWhenHelpButton t={t} method={method} size="small" />
                </div>
                <div className="mcc-tiles">
                  {methodEvents.map((event) => {
                    const key = event.event_key || event.key;
                    const when = eventWhen(key);
                    const published = !!(
                      event.channels &&
                      ((event.channels.sms && event.channels.sms.published) ||
                        (event.channels.email && event.channels.email.published))
                    );
                    return (
                      <button
                        type="button"
                        key={key}
                        className="mcc-tile"
                        onClick={() => openEvent({ key })}
                      >
                        <h3>{event.label}</h3>
                        {when ? (
                          <p className="mcc-tile-blurb">
                            {t(`MessageControlHelp.When.${key}.Body`, {
                              defaultValue: when,
                            })}
                          </p>
                        ) : null}
                        <div
                          className={`mcc-tile-status${
                            published ? ' is-published' : ''
                          }`}
                        >
                          {published
                            ? t('MessageControl.Published', {
                                defaultValue: 'Published',
                              })
                            : t('MessageControl.Draft', {
                                defaultValue: 'Draft',
                              })}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <React.Fragment>
            {query.from === 'list' ? null : (
            <Tabs
              value={channel}
              onChange={(_event, next) => {
                setError('');
                if (next === 'mobile') {
                  const mobileKeys = eventsForMethod(method, 'mobile').map(
                    (item) => item.key
                  );
                  if (mobileKeys.indexOf(eventKey) === -1) {
                    setChannel('sms');
                    return;
                  }
                }
                setChannel(next);
              }}
              className="mcc-channel-tabs"
              variant="scrollable"
              scrollButtons="auto"
              TabIndicatorProps={{ className: 'mcc-channel-tabs-indicator' }}
            >
              <Tab
                className="mcc-channel-tab"
                value="sms"
                icon={<SmsOutlined fontSize="small" />}
                iconPosition="start"
                label="SMS"
              />
              <Tab
                className="mcc-channel-tab"
                value="email"
                icon={<EmailOutlined fontSize="small" />}
                iconPosition="start"
                label="Email"
              />
              {eventsForMethod(method, 'mobile').some(
                (item) => item.key === eventKey
              ) ? (
              <Tab
                className="mcc-channel-tab"
                value="mobile"
                icon={<LanguageOutlined fontSize="small" />}
                iconPosition="start"
                label={t('MessageControl.Mobile', { defaultValue: 'Link message' })}
              />
              ) : null}
              <Tab
                className="mcc-channel-tab"
                value="notes"
                icon={<NotesOutlined fontSize="small" />}
                iconPosition="start"
                label={t('MessageControl.Notes', { defaultValue: 'Custom notes' })}
              />
            </Tabs>
            )}
            {eventWhen(eventKey) ? (
              <div className="mcc-when-banner">
                <HelpOutline fontSize="small" aria-hidden="true" />
                <p>
                  {t(`MessageControlHelp.When.${eventKey}.Body`, {
                    defaultValue: eventWhen(eventKey),
                  })}
                </p>
              </div>
            ) : null}
            {error ? <p className="mcc-error">{error}</p> : null}
            {busyAction === 'load' && !templates ? (
              <div className="mcc-editor-skeleton">
                <span className="mcc-skeleton-bar" />
                <span className="mcc-skeleton-bar" />
                <span className="mcc-skeleton-bar" />
              </div>
            ) : null}
            {channel === 'sms' || channel === 'email' ? (
              <div className="mcc-editor-grid is-three">
                <section className="mcc-panel mcc-customize-pane">
                  <h2 className="mcc-panel-title">
                    {t('MessageControl.Customization', {
                      defaultValue: 'Customization',
                    })}
                  </h2>
                  <p className="mcc-helper">
                    {channel === 'sms'
                      ? t('MessageControl.SmsHelper', {
                          defaultValue:
                            'SMS is composed in the HTML editor. Locked tokens cannot be deleted. The patient receives plain text.',
                        })
                      : t('MessageControl.EmailHelper', {
                          defaultValue:
                            'Pick branding colors and cards here. The editor in the middle is the email body. Locked pickup code and buttons stay in place.',
                        })}
                  </p>
                  {renderDefaultToggle()}
                  {channel === 'email' ? (
                    <TextField
                      className="mcc-subject-field"
                      fullWidth
                      size="small"
                      margin="dense"
                      label={t('MessageControl.Subject', {
                        defaultValue: 'Subject',
                      })}
                      value={subject}
                      onChange={(event) => setSubject(event.target.value)}
                    />
                  ) : null}
                  <p className="mcc-token-help">
                    {t('MessageControl.TokenHelper', {
                      defaultValue:
                        'A token is a merge field filled from the database at send time. Click a chip to insert it. Only listed keys are resolved.',
                    })}
                  </p>
                  <div className="mcc-token-legend">
                    {editorTokens
                      .filter(
                        (token) => channel !== 'email' || token.name !== 'STOP'
                      )
                      .map((token) => (
                      <button
                        type="button"
                        className="mcc-token-chip"
                        key={token.name}
                        title={token.source}
                        onClick={() => insertTokenAtCursor(token.name)}
                      >
                        {`{{${token.name}}}`}
                      </button>
                    ))}
                  </div>
                  {channel === 'email' ? (
                    <EmailLayoutTools
                      t={t}
                      colors={brandColors}
                      onChangeColor={handleBrandColor}
                      onAddCard={insertLayoutCard}
                      hideAmountDue={hideAmountDue}
                      paymentEnabled={paymentEnabled}
                      onToggleHideAmount={toggleHideAmount}
                      hasOrderSummary={hasOrderSummary}
                      summaryConfig={summaryConfig}
                      onChangeSummaryConfig={applySummaryConfig}
                    />
                  ) : null}
                </section>
                <section className="mcc-panel mcc-editor-pane">
                  <div className="mcc-panel-head">
                    <h2 className="mcc-panel-title">
                      {t('MessageControl.HtmlEditor', {
                        defaultValue: 'HTML Editor',
                      })}
                    </h2>
                    <div className="mcc-view-toggle">
                      <Button
                        className={`mcc-view-toggle-btn${!sourceView ? ' is-active' : ''}`}
                        variant={!sourceView ? 'contained' : 'outlined'}
                        onClick={() => setSourceView(false)}
                      >
                        Design
                      </Button>
                      <Button
                        className={`mcc-view-toggle-btn${sourceView ? ' is-active' : ''}`}
                        variant={sourceView ? 'contained' : 'outlined'}
                        onClick={() => setSourceView(true)}
                      >
                        HTML source
                      </Button>
                    </div>
                  </div>
                  {sourceView ? (
                    <textarea
                      className="mcc-source"
                      value={html}
                      onChange={(event) => setHtml(event.target.value)}
                    />
                  ) : (
                    <HtmlEditor
                      key={`mcc-html-${channel}-layout-v13`}
                      ref={editorRef}
                      value={html}
                      onChange={setHtml}
                      preset={channel}
                      fillHeight
                      height="100%"
                      enableImages={channel === 'email'}
                      tokens={
                        channel === 'email'
                          ? editorTokens.filter((token) => token.name !== 'STOP')
                          : editorTokens
                      }
                      layoutCards={editorCards}
                      onSelectToken={(token) => {
                        insertTokenAtCursor(token && token.name);
                        return true;
                      }}
                      onInsertCard={insertLayoutCard}
                      uploadImage={(file) => uploadMessageImage(file, boxId)}
                    />
                  )}
                </section>
                {renderPreviewPane()}
              </div>
            ) : null}
            {channel === 'mobile' ? (
              <div className="mcc-editor-grid is-link">
                <div className="mcc-scroll-pane">
                  <p className="mcc-helper">
                    {t('MessageControl.MobileHelper', {
                      defaultValue:
                          'This is the page patients open after they tap the link in the SMS or email. Greeting, custom message, steps copy, pay button, colors, and pharmacy logo are yours. Address, method switcher, Video Consult, Capture ID, signature, Checkout, store hours, and Powered by ilocalbox.com stay locked.',
                    })}
                  </p>
                  {renderDefaultToggle()}
                  <div className="mcc-link-section">
                    <h3>
                      {t('MessageControl.Greeting', {
                        defaultValue: 'Greeting',
                      })}
                    </h3>
                    <p className="mcc-helper">
                      {t('MessageControl.GreetingHelp', {
                        defaultValue:
                          'Shown after the locked “Hi {First Name},”. Default is “we have you ready for Curbside pick up.”',
                      })}
                    </p>
                    <TextField
                      fullWidth
                      margin="normal"
                      label={t('MessageControl.GreetingLine', {
                        defaultValue: 'Greeting line',
                      })}
                      value={greeting}
                      inputProps={{ maxLength: 80 }}
                      helperText={`${greeting.length} / 80`}
                      onChange={(event) =>
                        setGreeting(event.target.value.slice(0, 80))
                      }
                    />
                  </div>
                  <div className="mcc-link-section">
                    <h3>
                      {t('MessageControl.LinkCustomMessage', {
                        defaultValue: 'Custom message',
                      })}
                    </h3>
                    <p className="mcc-helper">
                      {t('MessageControl.LinkCustomMessageHelp', {
                        defaultValue:
                          'Shown on the patient page under the address. Hidden if Studio is not licensed or this is empty.',
                      })}
                    </p>
                    <TextField
                      fullWidth
                      margin="normal"
                      multiline
                      minRows={3}
                      label={t('MessageControl.LinkCustomMessageLine', {
                        defaultValue: 'Custom message',
                      })}
                      value={customMessage}
                      inputProps={{ maxLength: LINK_CUSTOM_MESSAGE_MAX }}
                      helperText={`${customMessage.length} / ${LINK_CUSTOM_MESSAGE_MAX}`}
                      onChange={(event) =>
                        setCustomMessage(
                          event.target.value.slice(0, LINK_CUSTOM_MESSAGE_MAX)
                        )
                      }
                    />
                  </div>
                  <div className="mcc-link-section">
                    <h3>
                      {t('MessageControl.StepsCard', {
                        defaultValue: 'Required steps card',
                      })}
                    </h3>
                    <TextField
                      fullWidth
                      margin="normal"
                      label={t('MessageControl.Headline', {
                        defaultValue: 'Steps title',
                      })}
                      value={headline}
                      inputProps={{ maxLength: 80 }}
                      helperText={`${headline.length} / 80`}
                      onChange={(event) =>
                        setHeadline(event.target.value.slice(0, 80))
                      }
                    />
                    <TextField
                      fullWidth
                      margin="normal"
                      multiline
                      minRows={4}
                      label={t('MessageControl.Intro', {
                        defaultValue: 'Steps helper copy',
                      })}
                      value={intro}
                      inputProps={{ maxLength: 240 }}
                      helperText={`${intro.length} / 240`}
                      onChange={(event) =>
                        setIntro(event.target.value.slice(0, 240))
                      }
                    />
                  </div>
                  <div className="mcc-link-section">
                    <h3>
                      {t('MessageControl.Buttons', {
                        defaultValue: 'Buttons',
                      })}
                    </h3>
                    {paymentEnabled ? (
                      <TextField
                        fullWidth
                        margin="normal"
                        label={t('MessageControl.Cta', {
                          defaultValue: 'Pay button label',
                        })}
                        value={ctaLabel}
                        inputProps={{ maxLength: 40 }}
                        helperText={`${ctaLabel.length} / 40`}
                        onChange={(event) =>
                          setCtaLabel(event.target.value.slice(0, 40))
                        }
                      />
                    ) : null}
                    <TextField
                      fullWidth
                      margin="normal"
                      label={t('MessageControl.ArrivalCta', {
                        defaultValue: 'After-checkout button (I am here)',
                      })}
                      value={arrivalLabel}
                      inputProps={{ maxLength: 40 }}
                      helperText={`${arrivalLabel.length} / 40`}
                      onChange={(event) =>
                        setArrivalLabel(event.target.value.slice(0, 40))
                      }
                    />
                  </div>
                  <div className="mcc-link-section">
                    <h3>
                      {t('MessageControl.LinkColors', {
                        defaultValue: 'Colors',
                      })}
                    </h3>
                    <div className="mcc-color-grid">
                      {[
                        { key: 'button', label: 'Pay button' },
                        { key: 'accent', label: 'Accent (badge & steps)' },
                      ]
                        .filter((field) => paymentEnabled || field.key !== 'button')
                        .map((field) => (
                        <label className="mcc-color-field" key={field.key}>
                          <span>
                            {t(`MessageControl.LinkColor.${field.key}`, {
                              defaultValue: field.label,
                            })}
                          </span>
                          <span className="mcc-color-controls">
                            <input
                              type="color"
                              aria-label={field.label}
                              value={normalizeHex(
                                linkColors[field.key],
                                DEFAULT_LINK_COLORS[field.key]
                              )}
                              onChange={(event) =>
                                handleLinkColor(field.key, event.target.value)
                              }
                            />
                            <input
                              type="text"
                              className="mcc-color-hex"
                              maxLength={7}
                              value={linkColors[field.key] || ''}
                              onChange={(event) =>
                                handleLinkColor(field.key, event.target.value)
                              }
                            />
                          </span>
                        </label>
                      ))}
                    </div>
                    {paymentEnabled ? (
                      <label className="mcc-hide-amount">
                        <input
                          type="checkbox"
                          checked={hideAmountDueLink}
                          onChange={(event) =>
                            setHideAmountDueLink(event.target.checked)
                          }
                        />
                        <span>
                          {t('MessageControl.HideAmountDue', {
                            defaultValue: 'Hide amount due on this page',
                          })}
                        </span>
                      </label>
                    ) : null}
                    <div className="mcc-link-logo-tools">
                      <h3>
                        {t('MessageControl.LinkLogo', {
                          defaultValue: 'Pharmacy logo',
                        })}
                      </h3>
                      <p className="mcc-helper">
                        {t('MessageControl.LinkLogoHelp', {
                          defaultValue:
                            'Shown at the top of the patient link page. If empty, the location brand logo is used. Powered by ilocalbox.com stays locked.',
                        })}
                      </p>
                      {linkLogoUrl ? (
                        <img
                          className="mcc-link-logo-preview"
                          src={linkLogoUrl}
                          alt=""
                        />
                      ) : null}
                      <input
                        ref={linkLogoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        hidden
                        onChange={(event) => {
                          const file =
                            event.target.files && event.target.files[0];
                          event.target.value = '';
                          uploadLinkLogo(file);
                        }}
                      />
                      <div className="mcc-link-logo-actions">
                        <Button
                          type="button"
                          variant="outlined"
                          size="small"
                          disabled={linkLogoBusy || !boxId}
                          onClick={() => {
                            if (linkLogoInputRef.current) {
                              linkLogoInputRef.current.click();
                            }
                          }}
                        >
                          {linkLogoBusy
                            ? t('MessageControl.PharmacyLogoBusy', {
                                defaultValue: 'Uploading logo…',
                              })
                            : linkLogoUrl
                              ? t('MessageControl.PharmacyLogoChange', {
                                  defaultValue: 'Replace logo',
                                })
                              : t('MessageControl.PharmacyLogoUpload', {
                                  defaultValue: 'Upload logo',
                                })}
                        </Button>
                        {linkLogoUrl ? (
                          <Button
                            type="button"
                            size="small"
                            disabled={linkLogoBusy}
                            onClick={() => setLinkLogoUrl('')}
                          >
                            {t('MessageControl.LinkLogoRemove', {
                              defaultValue: 'Remove logo',
                            })}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="mcc-locked-chrome">
                    {t('MessageControl.MobileLocked', {
                      defaultValue:
                        'Locked on the patient page: address, pickup switching, Video Consult, Capture ID, signature pad, Checkout, store hours, and Powered by ilocalbox.com.',
                    })}
                  </div>
                </div>
                {renderPreviewPane()}
              </div>
            ) : null}
            {channel === 'notes' ? (
              <div className="mcc-scroll-pane">
                <div className="mcc-notes-layout">
                  <div className="mcc-notes-banner">
                    <p>
                      {t('MessageControl.PhiHelper', {
                        defaultValue:
                          'Do not include patient health information in this note.',
                      })}
                    </p>
                    <span className="mcc-notes-count">
                      {t('MessageControl.NotesUsed', {
                        count: alternateNotes.length,
                        defaultValue: '{{count}} of 5 alternate notes used',
                      })}
                    </span>
                  </div>
                  <div className="mcc-notes-list">
                    {notes.map((note) => {
                      const noteBusy =
                        busyNoteId === note.id &&
                        (busyAction === 'note-save' || busyAction === 'note-delete');
                      return (
                      <div
                        className={`mcc-note-card${note.is_default ? ' is-default' : ''}${
                          note.is_enabled ? '' : ' is-disabled'
                        }${noteBusy ? ' is-busy' : ''}`}
                        key={note.id}
                      >
                        {noteBusy ? (
                          <div className="mcc-note-card-busy">
                            <CircularProgress size={20} />
                            <span>
                              {busyAction === 'note-delete'
                                ? t('MessageControl.BusyNoteDelete', {
                                    defaultValue: 'Deleting note…',
                                  })
                                : t('MessageControl.BusyNoteSave', {
                                    defaultValue: 'Saving note…',
                                  })}
                            </span>
                          </div>
                        ) : null}
                        <div className="mcc-note-card-main">
                          <div className="mcc-note-card-meta">
                            <strong className="mcc-note-label">{note.label}</strong>
                            {note.is_default ? (
                              <span className="mcc-note-badge">
                                {t('MessageControl.Default', { defaultValue: 'Default' })}
                              </span>
                            ) : null}
                            {!note.is_enabled ? (
                              <span className="mcc-note-badge is-muted">
                                {t('MessageControl.Disabled', { defaultValue: 'Disabled' })}
                              </span>
                            ) : null}
                          </div>
                          <p className="mcc-note-body">
                            {note.body ||
                              t('MessageControl.EmptyNote', {
                                defaultValue: 'No note text yet',
                              })}
                          </p>
                        </div>
                        <div className="mcc-note-card-actions">
                          {!note.is_default ? (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={busy}
                              startIcon={
                                <ActionSpinner
                                  active={
                                    busyAction === 'note-save' &&
                                    busyNoteId === note.id
                                  }
                                />
                              }
                              onClick={() =>
                                saveNote({
                                  id: note.id,
                                  is_default: true,
                                  body: note.body,
                                  label: note.label,
                                })
                              }
                            >
                              {t('MessageControl.SetDefault', {
                                defaultValue: 'Set as default',
                              })}
                            </Button>
                          ) : null}
                          <Button
                            size="small"
                            disabled={busy}
                            startIcon={
                              <ActionSpinner
                                active={
                                  busyAction === 'note-save' &&
                                  busyNoteId === note.id
                                }
                              />
                            }
                            onClick={() =>
                              saveNote({
                                id: note.id,
                                is_enabled: !note.is_enabled,
                                body: note.body,
                                label: note.label,
                              })
                            }
                          >
                            {note.is_enabled
                              ? t('MessageControl.Disable', { defaultValue: 'Disable' })
                              : t('MessageControl.Enable', { defaultValue: 'Enable' })}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            disabled={busy || notes.length <= 1}
                            onClick={() => setNoteToDelete(note)}
                          >
                            {t('MessageControl.DeleteNote', { defaultValue: 'Delete' })}
                          </Button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                  <div className="mcc-note-composer">
                    <h3>
                      {t('MessageControl.AddNoteTitle', {
                        defaultValue: 'Add an alternate note',
                      })}
                    </h3>
                    <TextField
                      fullWidth
                      margin="normal"
                      label={t('MessageControl.NoteLabel', { defaultValue: 'Label' })}
                      value={noteDraft.label}
                      onChange={(event) =>
                        setNoteDraft({ ...noteDraft, label: event.target.value })
                      }
                    />
                    <TextField
                      fullWidth
                      margin="normal"
                      multiline
                      label={t('MessageControl.NoteBody', { defaultValue: 'Note' })}
                      value={noteDraft.body}
                      inputProps={{ maxLength: 120 }}
                      helperText={`${noteDraft.body.length} / 120`}
                      onChange={(event) =>
                        setNoteDraft({
                          ...noteDraft,
                          body: event.target.value.slice(0, 120),
                        })
                      }
                    />
                    <Button
                      variant="contained"
                      disabled={busy || !canAddAlternate}
                      startIcon={
                        <ActionSpinner
                          active={busyAction === 'note-save' && busyNoteId === 'new'}
                        />
                      }
                      onClick={() =>
                        saveNote({
                          label: noteDraft.label || 'Alternate',
                          body: noteDraft.body,
                          is_default: notes.length === 0,
                        })
                      }
                    >
                      {t('MessageControl.AddNote', {
                        defaultValue: 'Add alternate note',
                      })}
                    </Button>
                  </div>
                </div>
              </div>
            ) : null}
              </React.Fragment>
            )}
          </DialogContent>
          {eventKey ? (
          <DialogActions className="mcc-dialog-footer">
            {channel !== 'notes' ? (
              <React.Fragment>
                <Button
                  onClick={restore}
                  disabled={busy}
                  startIcon={<ActionSpinner active={busyAction === 'restore'} />}
                >
                  {busyAction === 'restore'
                    ? t('MessageControl.BusyRestore', {
                        defaultValue: 'Restoring default…',
                      })
                    : t('MessageControl.Restore', { defaultValue: 'Restore default' })}
                </Button>
                <Button
                  onClick={saveDraft}
                  variant="outlined"
                  disabled={busy}
                  startIcon={<ActionSpinner active={busyAction === 'save'} />}
                >
                  {busyAction === 'save'
                    ? t('MessageControl.BusySave', { defaultValue: 'Saving draft…' })
                    : t('MessageControl.SaveDraft', { defaultValue: 'Save as draft' })}
                </Button>
                <Button
                  onClick={publish}
                  variant="contained"
                  disabled={busy || !!(preview && preview.blocked)}
                  startIcon={<ActionSpinner active={busyAction === 'publish'} />}
                >
                  {busyAction === 'publish'
                    ? t('MessageControl.BusyPublish', { defaultValue: 'Publishing…' })
                    : t('MessageControl.Publish', { defaultValue: 'Publish' })}
                </Button>
              </React.Fragment>
            ) : null}
            <Button
              onClick={requestClose}
              disabled={busy}
              variant="outlined"
            >
              {t('MessageControl.Close', { defaultValue: 'Close' })}
            </Button>
          </DialogActions>
          ) : null}
        </div>
      </Dialog>
      <ThemedConfirmDialog
        open={!!noteToDelete}
        title={t('MessageControl.DeleteNoteTitle', {
          defaultValue: 'Delete custom note',
        })}
        message={
          noteToDelete && noteToDelete.is_default
            ? t('MessageControl.DeleteDefaultNoteConfirm', {
                defaultValue:
                  'Delete this default note? Another enabled note will become the default.',
              })
            : t('MessageControl.DeleteNoteConfirm', {
                defaultValue: 'Delete this custom note?',
              })
        }
        cancelLabel={t('MessageControl.Cancel', { defaultValue: 'Cancel' })}
        confirmLabel={t('MessageControl.DeleteNote', { defaultValue: 'Delete' })}
        confirmBusyLabel={t('MessageControl.BusyNoteDelete', {
          defaultValue: 'Deleting note…',
        })}
        busy={!!deletingNote}
        onCancel={() => {
          if (!busy) setNoteToDelete(null);
        }}
        onConfirm={() => deleteNote(noteToDelete)}
      />
      <ThemedConfirmDialog
        open={leavePrompt}
        title={t('MessageControl.SaveChangesTitle', {
          defaultValue: 'Save changes?',
        })}
        message={t('MessageControl.SaveChangesBody', {
          defaultValue:
            'This template has changes that are not published. Save them as a draft before closing?',
        })}
        cancelLabel={t('MessageControl.No', { defaultValue: 'No' })}
        confirmLabel={t('MessageControl.Yes', { defaultValue: 'Yes' })}
        confirmBusyLabel={t('MessageControl.BusySave', {
          defaultValue: 'Saving draft…',
        })}
        busy={busyAction === 'save'}
        disableEscapeKeyDown
        onCancel={closeWithoutSaving}
        onConfirm={saveDraftAndClose}
      />
      <AccountLocationPickerDialog
        open={!!tokenPicker}
        t={t}
        token={tokenPicker}
        accounts={accounts}
        locations={locations}
        accountId={accountId}
        boxId={boxId}
        onCancel={() => setTokenPicker(null)}
        onConfirm={(selection) => {
          setPickedPreview({
            accountName:
              (selection.account &&
                (selection.account.brandName || selection.account.name)) ||
              '',
            locationName: (selection.location && selection.location.name) || '',
          });
          insertResolvedToken(selection.token);
          setTokenPicker(null);
        }}
      />
    </div>
  );
};

const MessageControlCenter = ({ t }) => (
  <Location>
    {({ location }) => <MessageControlCenterPage t={t} location={location} />}
  </Location>
);

export default withTranslation()(MessageControlCenter);



// WEBPACK FOOTER //
// ./src/components/message-control-center/index.js